import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type Cell,
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnOrderState,
  type ColumnPinningState,
  type ColumnSizingState,
  type Header,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowDown, ArrowUp, ChevronsUpDown, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState } from '@/components/feedback/EmptyState';
import { cn } from '@/lib/utils/cn';
import { exportTableAsCsv } from './csv-export';
import { DataTableToolbar } from './DataTableToolbar';
import { useTablePrefs, type TablePrefs } from './use-table-prefs';

interface DataTableProps<TData, TValue> {
  /** Stable identifier — used as the localStorage key for persisted prefs. */
  tableId: string;
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  searchPlaceholder?: string;
  searchColumnId?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  pageSize?: number;
  toolbar?: React.ReactNode;
  /** When set, a selection checkbox column is prepended. */
  enableRowSelection?: boolean;
  /** Render a bulk action bar when rows are selected. */
  renderSelectionActions?: (selectedIds: string[], clear: () => void) => React.ReactNode;
  /** Hook called when a row is clicked (e.g. navigate to detail). */
  onRowClick?: (row: TData) => void;
  /** Resolve a unique id per row (defaults to the row index). */
  getRowId?: (row: TData, index: number) => string;
  /** Disable CSV export button. */
  disableExport?: boolean;
  /** Filename for CSV download. */
  exportFilename?: string;
  /**
   * Default column visibility on first render. Users can still toggle
   * any column on/off via the Columns dropdown; their preferences are
   * persisted per `tableId`. This map only seeds the FIRST visit.
   */
  initialColumnVisibility?: VisibilityState;
  /**
   * Pin columns to the left or right edge so they remain visible while
   * the rest of the table scrolls horizontally. Pinned columns are
   * excluded from drag-reorder.
   */
  initialColumnPinning?: ColumnPinningState;
  /**
   * Renders between the toolbar and the table — useful for feature-
   * specific filter rows that should sit visually above the table but
   * below the table controls (search / page size / density / columns).
   */
  beforeTable?: React.ReactNode;
}

export function DataTable<TData, TValue>({
  tableId,
  columns: userColumns,
  data,
  isLoading = false,
  searchPlaceholder,
  searchColumnId,
  emptyTitle = 'No records',
  emptyDescription = 'Nothing to show with the current filters.',
  pageSize: pageSizeProp = 25,
  toolbar,
  enableRowSelection = false,
  renderSelectionActions,
  onRowClick,
  getRowId,
  disableExport = false,
  exportFilename = 'export.csv',
  initialColumnVisibility,
  initialColumnPinning,
  beforeTable,
}: DataTableProps<TData, TValue>) {
  const [prefs, setPrefs] = useTablePrefs(tableId);

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});

  // Build the columns with optional selection column.
  const columns = React.useMemo<ColumnDef<TData, TValue>[]>(() => {
    if (!enableRowSelection) return userColumns;
    return [
      {
        id: 'select',
        size: 36,
        enableHiding: false,
        enableSorting: false,
        enableResizing: false,
        header: ({ table }) => (
          <Checkbox
            aria-label="Select all"
            checked={
              table.getIsAllPageRowsSelected()
                ? true
                : table.getIsSomePageRowsSelected()
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={(v) => table.toggleAllPageRowsSelected(Boolean(v))}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            aria-label="Select row"
            checked={row.getIsSelected()}
            onCheckedChange={(v) => row.toggleSelected(Boolean(v))}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      ...userColumns,
    ];
  }, [enableRowSelection, userColumns]);

  const [columnOrder, setColumnOrder] = React.useState<ColumnOrderState>(() => {
    if (prefs.columnOrder?.length) {
      // Saved order may pre-date newly added columns — slot them in at their
      // default position (right after the column that precedes them) instead
      // of letting TanStack park them at the far end.
      const defaultIds = columns.map((c, i) => {
        const def = c as { id?: string; accessorKey?: string };
        if (typeof def.id === 'string') return def.id;
        if (typeof def.accessorKey === 'string') return def.accessorKey;
        return `col-${i}`;
      });
      const merged = prefs.columnOrder.filter((id) => defaultIds.includes(id));
      defaultIds.forEach((id, i) => {
        if (merged.includes(id)) return;
        const prev = i > 0 ? defaultIds[i - 1] : undefined;
        const at = prev ? merged.indexOf(prev) : -1;
        merged.splice(at + 1, 0, id);
      });
      return merged;
    }
    // TanStack uses `accessorKey` as the column id when no explicit `id` is set.
    // The previous version only read `.id` and fell back to "col-N" placeholders,
    // which didn't match the real column ids → TanStack ignored most entries
    // and applied only the explicit-id columns, leaving everything else in
    // natural order. Reading accessorKey too produces the correct order.
    return columns.map((c, i) => {
      const def = c as { id?: string; accessorKey?: string };
      if (typeof def.id === 'string') return def.id;
      if (typeof def.accessorKey === 'string') return def.accessorKey;
      return `col-${i}`;
    });
  });
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(
    prefs.columnVisibility ?? initialColumnVisibility ?? {},
  );
  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>(
    prefs.columnSizing ?? {},
  );
  const [columnPinning, setColumnPinning] = React.useState<ColumnPinningState>(
    initialColumnPinning ?? { left: [], right: [] },
  );
  const density: NonNullable<TablePrefs['density']> = prefs.density ?? 'comfortable';

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
      columnOrder,
      columnSizing,
      columnPinning,
      rowSelection,
    },
    initialState: {
      pagination: { pageSize: prefs.pageSize ?? pageSizeProp },
    },
    enableRowSelection,
    enableMultiSort: true,
    enableColumnResizing: true,
    enableColumnPinning: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        setPrefs({ ...prefs, columnVisibility: next });
        return next;
      });
    },
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: (updater) => {
      setColumnSizing((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        setPrefs({ ...prefs, columnSizing: next });
        return next;
      });
    },
    onRowSelectionChange: setRowSelection,
    onColumnPinningChange: setColumnPinning,
    ...(getRowId ? { getRowId } : {}),
  });

  // DnD for header reorder
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = columnOrder.indexOf(String(active.id));
    const newIndex = columnOrder.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(columnOrder, oldIndex, newIndex);
    setColumnOrder(next);
    setPrefs({ ...prefs, columnOrder: next });
  };

  const onDensityChange = (next: NonNullable<TablePrefs['density']>) => {
    setPrefs({ ...prefs, density: next });
  };

  const selectedIds = Object.keys(rowSelection);
  const total = data.length;
  const pageIndex = table.getState().pagination.pageIndex;
  const ps = table.getState().pagination.pageSize;
  const pageCount = table.getPageCount();

  const cellPadding = {
    compact: 'py-1.5',
    comfortable: 'py-2.5',
    spacious: 'py-4',
  }[density];

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <DataTableToolbar
        table={table}
        density={density}
        onDensityChange={onDensityChange}
        searchPlaceholder={searchPlaceholder ?? 'Search…'}
        {...(searchColumnId ? { searchColumnId } : {})}
        toolbar={toolbar}
        {...(disableExport ? {} : { onExport: () => exportTableAsCsv(table, exportFilename) })}
        pageSize={ps}
        pageSizeOptions={[10, 25, 50, 100]}
        onPageSizeChange={(n) => {
          table.setPageSize(n);
          setPrefs({ ...prefs, pageSize: n });
        }}
      />

      {beforeTable}

      {enableRowSelection && selectedIds.length > 0 && (
        <div className="flex items-center justify-between rounded-md border bg-accent/40 px-4 py-2 text-sm">
          <span className="font-medium">
            {selectedIds.length} selected
          </span>
          <div className="flex items-center gap-2">
            {renderSelectionActions?.(selectedIds, () => setRowSelection({}))}
            <Button variant="ghost" size="sm" onClick={() => setRowSelection({})}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {/*
        Single scroll region for both axes. This is intentional: a CSS
        scroll container can't pin sticky to anything outside itself, so
        the only way to have both a sticky header AND horizontal scroll
        is to put both inside the same container. The TableHeader sticks
        to the top of THIS container; pinned columns stick to the right.
       */}
      <div className="flex min-h-0 flex-1 flex-col rounded-lg border bg-card">
        <div className="min-h-0 flex-1 overflow-auto">
          <Table containerClassName="overflow-visible">
            <TableHeader className="sticky top-0 z-30 bg-muted">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToHorizontalAxis]}
                onDragEnd={handleDragEnd}
              >
                {table.getHeaderGroups().map((headerGroup) => {
                  // Only non-pinned headers participate in drag reorder; the
                  // pinned ones stay locked to their edge.
                  const sortableIds = headerGroup.headers
                    .filter((h) => !h.column.getIsPinned())
                    .map((h) => h.id);
                  return (
                    <TableRow key={headerGroup.id} className="hover:bg-transparent">
                      <SortableContext
                        items={sortableIds}
                        strategy={horizontalListSortingStrategy}
                      >
                        {headerGroup.headers.map((header) => (
                          <DraggableHeaderCell
                            key={header.id}
                            headerId={header.id}
                            pinned={header.column.getIsPinned()}
                            stickyStyle={getStickyStyle(header, 'header')}
                          >
                            {header.isPlaceholder ? null : (
                              <HeaderContent
                                canSort={header.column.getCanSort()}
                                sorted={header.column.getIsSorted()}
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </HeaderContent>
                            )}
                            {header.column.getCanResize() && (
                              <span
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                className={cn(
                                  'absolute right-0 top-0 z-20 h-full w-1.5 cursor-col-resize select-none touch-none bg-transparent transition-colors hover:bg-primary/30',
                                  header.column.getIsResizing() && 'bg-primary/60',
                                )}
                              />
                            )}
                          </DraggableHeaderCell>
                        ))}
                      </SortableContext>
                    </TableRow>
                  );
                })}
              </DndContext>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, idx) => (
                  <TableRow key={`skeleton-${idx}`} className="hover:bg-transparent">
                    {table.getVisibleLeafColumns().map((c) => (
                      <TableCell key={c.id} className={cellPadding}>
                        <Skeleton className="h-4 w-3/4" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : table.getRowModel().rows.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={table.getVisibleLeafColumns().length} className="p-0">
                    <EmptyState title={emptyTitle} description={emptyDescription} className="border-0" />
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() ? 'selected' : undefined}
                    onClick={() => onRowClick?.(row.original)}
                    className={cn(
                      // Solid baseline + solid hover so pinned cells stay
                      // visually identical to the rest of the row at every
                      // interaction state.
                      'group/row bg-card hover:bg-muted data-[state=selected]:bg-accent',
                      onRowClick && 'cursor-pointer',
                    )}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const pinned = cell.column.getIsPinned();
                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            cellPadding,
                            // Pinned cells need a fully opaque background on
                            // every state so scrolled content can NEVER show
                            // through. Using solid `bg-card` / `bg-muted` /
                            // `bg-accent` (no alpha) means even on row hover
                            // the pinned column reads as a clean column,
                            // never a translucent overlay.
                            pinned &&
                              'bg-card group-hover/row:bg-muted group-data-[state=selected]/row:bg-accent',
                          )}
                          style={{
                            width: cell.column.getSize(),
                            ...getStickyStyle(cell, 'body'),
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>
            Showing{' '}
            <span className="font-medium text-foreground">
              {Math.min(total, pageIndex * ps + 1)}–{Math.min(total, (pageIndex + 1) * ps)}
            </span>{' '}
            of <span className="font-medium text-foreground">{total}</span>
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <span>
              Page {pageIndex + 1} / {Math.max(pageCount, 1)}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface DraggableHeaderCellProps {
  headerId: string;
  /** Pinned-edge ('left' | 'right' | false). Pinned cells aren't draggable. */
  pinned: false | 'left' | 'right';
  /** Sticky positioning style for the pinned cell. */
  stickyStyle?: React.CSSProperties;
  children: React.ReactNode;
}

function DraggableHeaderCell({
  headerId,
  pinned,
  stickyStyle,
  children,
}: DraggableHeaderCellProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: headerId,
    disabled: Boolean(pinned),
  });
  const style: React.CSSProperties = pinned
    ? // stickyStyle already includes `position: 'sticky'`.
      { ...stickyStyle }
    : {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        position: 'relative',
      };
  return (
    <TableHead
      ref={setNodeRef}
      style={style}
      className={cn(
        'relative whitespace-nowrap',
        // Pinned headers sit above body pinned cells. Solid bg-muted
        // (no opacity) so scrolled content under the sticky column
        // cannot bleed through.
        pinned && 'bg-muted',
      )}
    >
      {/*
       * Grip handle is positioned absolutely so it does NOT push the
       * header text rightward. Without this, the header content sits
       * ~22px further right than the body cell content (button width
       * + gap), making columns look asymmetric. On hover the grip
       * fades in over the left padding gutter (px-3 = 12px) and the
       * text stays visible because the icon is only 14px wide.
       */}
      {!pinned && (
        <button
          type="button"
          {...listeners}
          {...attributes}
          className="absolute left-1 top-1/2 -translate-y-1/2 cursor-grab rounded text-muted-foreground/60 opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100 active:cursor-grabbing"
          aria-label={`Reorder column ${headerId}`}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      )}
      <div className="flex items-center gap-1.5">{children}</div>
    </TableHead>
  );
}

interface HeaderContentProps {
  canSort: boolean;
  sorted: false | 'asc' | 'desc';
  onClick: ((e: unknown) => void) | undefined;
  children: React.ReactNode;
}

function HeaderContent({ canSort, sorted, onClick, children }: HeaderContentProps) {
  if (!canSort) return <span>{children}</span>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 hover:text-foreground',
        sorted && 'text-foreground',
      )}
    >
      {children}
      {sorted === 'asc' ? (
        <ArrowUp className="h-3 w-3" />
      ) : sorted === 'desc' ? (
        <ArrowDown className="h-3 w-3" />
      ) : (
        <ChevronsUpDown className="h-3 w-3 opacity-50" />
      )}
    </button>
  );
}

/**
 * Compute the sticky positioning style for a pinned header/cell. TanStack
 * tracks the cumulative pinned column widths so we can derive the right
 * (or left) offset directly. Adds a subtle inset shadow on the leading
 * edge of the leftmost/rightmost pinned column to visually separate it
 * from the scrolling area.
 */
function getStickyStyle<TData, TValue>(
  cellOrHeader: Cell<TData, TValue> | Header<TData, TValue>,
  variant: 'header' | 'body',
): React.CSSProperties | undefined {
  const pinned = cellOrHeader.column.getIsPinned();
  if (!pinned) return undefined;
  const isFirstRight =
    pinned === 'right' && cellOrHeader.column.getIsFirstColumn('right');
  const isLastLeft =
    pinned === 'left' && cellOrHeader.column.getIsLastColumn('left');
  const shadow = isFirstRight
    ? '-4px 0 6px -4px hsl(var(--foreground) / 0.12)'
    : isLastLeft
      ? '4px 0 6px -4px hsl(var(--foreground) / 0.12)'
      : undefined;
  return {
    position: 'sticky',
    left: pinned === 'left' ? `${cellOrHeader.column.getStart('left')}px` : undefined,
    right: pinned === 'right' ? `${cellOrHeader.column.getAfter('right')}px` : undefined,
    // Header sticky-row is z-30, regular body cells are z-0. Pinned body
    // needs to clear the regular body cells (z-20) so scrolled content
    // hides correctly; pinned header needs to clear the rest of the
    // sticky header row (z-40).
    zIndex: variant === 'header' ? 40 : 20,
    boxShadow: shadow,
  };
}
